import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Clock3 } from 'lucide-react';
import {
  INVITE_START_PLACEHOLDER,
  buildMaskedInviteStartValue,
  formatInviteStartInput,
  getSaoPauloNowParts,
  parseInviteStartInput,
} from '../lib/saoPauloDateTime';

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

const getInitialMonthDate = (value) => {
  const parsed = parseInviteStartInput(value);
  if (parsed?.datePart) {
    const [year, month] = parsed.datePart.split('-').map(Number);
    return new Date(year, month - 1, 1);
  }

  const { datePart } = getSaoPauloNowParts();
  const [year, month] = datePart.split('-').map(Number);
  return new Date(year, month - 1, 1);
};

const buildCalendarCells = (displayMonth) => {
  const year = displayMonth.getFullYear();
  const month = displayMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const firstWeekday = firstDay.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];

  for (let index = 0; index < firstWeekday; index += 1) {
    cells.push(null);
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const datePart = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    cells.push({ day, datePart });
  }

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  return cells;
};

export default function ScheduledDateTimePicker({
  id,
  label,
  value,
  onChange,
  disabled = false,
  error = '',
  helperText = '',
}) {
  const wrapperRef = useRef(null);
  const parsedValue = useMemo(() => parseInviteStartInput(value), [value]);
  const [isOpen, setIsOpen] = useState(false);
  const [displayMonth, setDisplayMonth] = useState(() => getInitialMonthDate(value));
  const { datePart: todayDatePart, timePart: currentTimePart } = useMemo(() => getSaoPauloNowParts(), []);
  const selectedDatePart = parsedValue?.datePart || '';
  const selectedTimePart = parsedValue?.timePart || '';
  const timeValue = selectedTimePart || currentTimePart;
  const calendarCells = useMemo(() => buildCalendarCells(displayMonth), [displayMonth]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setDisplayMonth(getInitialMonthDate(value));
    }
  }, [isOpen, value]);

  const handleTextInputChange = (event) => {
    onChange(formatInviteStartInput(event.target.value));
  };

  const handleDateSelect = (datePart) => {
    onChange(buildMaskedInviteStartValue(datePart, timeValue));
  };

  const handleTimeSelect = (event) => {
    const nextTime = event.target.value;
    onChange(buildMaskedInviteStartValue(selectedDatePart || todayDatePart, nextTime));
  };

  return (
    <div ref={wrapperRef} className="relative flex flex-col gap-1.5">
      {label && (
        <label htmlFor={id} className="text-xs font-bold text-slate-500 uppercase tracking-widest">
          {label}
        </label>
      )}

      <div className="relative">
        <input
          id={id}
          type="text"
          inputMode="numeric"
          placeholder={INVITE_START_PLACEHOLDER}
          value={value}
          onChange={handleTextInputChange}
          disabled={disabled}
          className={`w-full rounded-lg border bg-slate-950 py-3 pl-3 pr-12 text-white outline-none transition ${
            error ? 'border-rose-500' : 'border-slate-800 focus:border-white'
          } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        />
        <button
          type="button"
          aria-label="Abrir seletor de data e hora"
          onClick={() => !disabled && setIsOpen((current) => !current)}
          disabled={disabled}
          className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-slate-400 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          <CalendarDays size={18} />
        </button>
      </div>

      {(helperText || error) && (
        <span className={`text-[11px] ${error ? 'text-rose-400' : 'text-slate-500'}`}>
          {error || helperText}
        </span>
      )}

      {isOpen && (
        <div className="absolute left-0 top-full z-[120] mt-2 w-[320px] rounded-xl border border-slate-700 bg-slate-950 p-4 shadow-2xl shadow-black/50">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setDisplayMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-700 text-slate-300 transition hover:border-slate-500 hover:text-white"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-bold capitalize text-white">
              {new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(displayMonth)}
            </span>
            <button
              type="button"
              onClick={() => setDisplayMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-700 text-slate-300 transition hover:border-slate-500 hover:text-white"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="mb-2 grid grid-cols-7 gap-1">
            {WEEKDAY_LABELS.map((weekday) => (
              <span key={weekday} className="py-1 text-center text-[11px] font-bold uppercase text-slate-500">
                {weekday}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {calendarCells.map((cell, index) => {
              if (!cell) {
                return <div key={`empty-${index}`} className="h-9" />;
              }

              const isSelected = cell.datePart === selectedDatePart;
              const isPast = cell.datePart < todayDatePart;
              const isToday = cell.datePart === todayDatePart;

              return (
                <button
                  key={cell.datePart}
                  type="button"
                  onClick={() => handleDateSelect(cell.datePart)}
                  disabled={isPast}
                  className={`h-9 rounded-lg text-sm font-semibold transition ${
                    isSelected
                      ? 'border border-orange-500 bg-orange-500/15 text-orange-300'
                      : isToday
                        ? 'border border-sky-500/50 bg-sky-500/10 text-sky-200'
                        : 'border border-transparent text-slate-200 hover:border-slate-700 hover:bg-slate-900'
                  } ${isPast ? 'cursor-not-allowed text-slate-700' : ''}`}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>

          <div className="mt-4 rounded-lg border border-slate-800 bg-slate-900/70 p-3">
            <label htmlFor={`${id}-time`} className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400">
              <Clock3 size={14} />
              Hora
            </label>
            <input
              id={`${id}-time`}
              type="time"
              step={60}
              value={timeValue}
              onChange={handleTimeSelect}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 p-2 text-white outline-none transition focus:border-orange-500"
            />
            <p className="mt-2 text-[11px] text-slate-500">
              Selecione a data no calendário e ajuste a hora aqui. O horário considerado é São Paulo.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
